import { createRoute, ErrorComponent, useLoaderData, useParams } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import {
  PageInitialDataError,
  unwrapPageInitialData,
  type PageInitialData,
  type PageMetadata,
} from "~/app/api/pageData";
import { embeddedConfiguratorPageQueryOptions } from "~/app/api/pageData/embeddedProductConfiguratorPageQuery";
import type { EmbeddedProductConfiguratorPageInitialData } from "~/app/api/pageData/embeddedProductConfiguratorPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { HeadManager } from "~/app/shell/HeadManager";
import { rootRoute } from "~/app/router/rootRoute";
import { ConfiguratorPageEntry } from "~/pages/ConfiguratorPage/ConfiguratorPage";
import { UnauthorizedView } from "~/common/components/UnauthorizedView/UnauthorizedView";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { resolveApiUrl } from "~/utils/url/urlResolver";

const EMBEDDED_CONFIGURATOR_TOKEN_INVALID = "EMBEDDED_CONFIGURATOR_TOKEN_INVALID";
const EMBEDDED_CONFIGURATOR_ORIGIN_INVALID = "EMBEDDED_CONFIGURATOR_ORIGIN_INVALID";

export interface EmbeddedConfiguratorSearch {
  token: string | null;
}

export function parseEmbeddedConfiguratorSearch(search: Record<string, unknown>): EmbeddedConfiguratorSearch {
  return { token: typeof search.token === "string" && search.token !== "" ? search.token : null };
}

function EmbeddedConfiguratorRoute() {
  const params = useParams({ strict: false });
  const result = useLoaderData({
    from: "/embeddedProductConfigurator/$productCode",
  }) as PageInitialData<EmbeddedProductConfiguratorPageInitialData>;

  return (
    <StorefrontShellFrame variant="embedded" metadata={result.metadata}>
      <ConfiguratorPageEntry productCode={params.productCode} />
    </StorefrontShellFrame>
  );
}

/**
 * Minimal embedded error boundary: renders the structured 401 via {@link UnauthorizedView}, and
 * renders the structured invalid-origin 500 via TanStack Router's standard {@link ErrorComponent}.
 * Both cases replace the route-owned head with {@code noindex,nofollow}; all other loader errors are
 * rethrown so they surface normally.
 */
function EmbeddedConfiguratorErrorBoundary({ error }: { error: Error }) {
  if (isEmbeddedConfiguratorInvalidOrigin(error)) {
    const errorMetadata: PageMetadata = {
      title: "Schaeffler eCommerce Industrial",
      breadcrumbs: [],
      slots: [],
      serverMessages: [],
      head: {
        meta: [{ name: "robots", content: "noindex,nofollow" }],
        links: [],
        structuredData: [],
      },
    };

    return (
      <>
        <HeadManager metadata={errorMetadata} />
        <ErrorComponent error={error} />
      </>
    );
  }

  if (!isEmbeddedConfiguratorUnauthorized(error)) {
    throw error;
  }

  const unauthorizedMetadata: PageMetadata = {
    title: "Unauthorized",
    breadcrumbs: [],
    slots: [],
    serverMessages: [],
    head: {
      meta: [{ name: "robots", content: "noindex,nofollow" }],
      links: [],
      structuredData: [],
    },
  };

  return (
    <>
      <HeadManager metadata={unauthorizedMetadata} />
      <UnauthorizedView />
    </>
  );
}

/**
 * Decides whether a route-loader error is the embedded configurator's structured 401 (missing,
 * blank, invalid, or expired partner token). Only that case renders {@link UnauthorizedView}; any
 * other error is rethrown by the boundary so it surfaces through the normal router error path.
 */
export function isEmbeddedConfiguratorUnauthorized(error: Error): boolean {
  return (
    error instanceof PageInitialDataError &&
    error.pageError?.status === 401 &&
    error.pageError.code === EMBEDDED_CONFIGURATOR_TOKEN_INVALID
  );
}

function isEmbeddedConfiguratorInvalidOrigin(error: Error): boolean {
  return (
    error instanceof PageInitialDataError &&
    error.pageError?.status === 500 &&
    error.pageError.code === EMBEDDED_CONFIGURATOR_ORIGIN_INVALID
  );
}

export async function loadEmbeddedConfiguratorPageData(
  context: { queryClient: QueryClient },
  productCode: string,
  token: string | null,
) {
  const result = await context.queryClient.ensureQueryData(embeddedConfiguratorPageQueryOptions(productCode, token));

  const pageData = unwrapPageInitialData<EmbeddedProductConfiguratorPageInitialData>(result);

  await synchronizeEmbeddedConfiguratorSession(pageData.data);
  return pageData;
}

async function synchronizeEmbeddedConfiguratorSession(data: EmbeddedProductConfiguratorPageInitialData): Promise<void> {
  const response = await fetch(resolveApiUrl("/embeddedProductConfigurator/session/partner-context"), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    body: JSON.stringify({
      embeddedToken: data.embeddedToken,
      appId: data.appId,
    }),
  });
  window.sessionStorage.setItem("appId", data.appId);
  if (!response.ok) {
    throw new Error(`Failed to synchronize embedded configurator session: ${response.status}`);
  }
}

export const embeddedConfiguratorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/embeddedProductConfigurator/$productCode",
  validateSearch: parseEmbeddedConfiguratorSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params, deps }) =>
    loadEmbeddedConfiguratorPageData(context, params.productCode, deps.token),
  component: EmbeddedConfiguratorRoute,
  errorComponent: EmbeddedConfiguratorErrorBoundary,
});
